import { useState, useRef, useEffect } from 'react';
import {
  MagnifyingGlass as Search,
  Plus,
  Bell,
  CaretDown as ChevronDown,
  SignOut as LogOut,
  GearSix as Settings,
  Play,
  Pause,
  Stop,
  Clock,
  X,
  Gavel,
  Briefcase,
  Users,
  ChatTeardropDots,
  FileText,
  CurrencyGbp,
  CheckCircle,
  ArrowsClockwise,
  List as MenuIcon,
  Warning,
  Scales,
  ClipboardText,
  CalendarCheck,
  XCircle,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store';
import { useChambersMode, workspaceTypeLabel } from '@/lib/useChambersMode';
import { canCreateMatter } from '@/lib/pupilUiBoundary';
import { useLogout } from '@/features/auth/hooks';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useTimerStore, formatElapsed } from '@/lib/timerStore';
import { TimeEntryModal } from '@/components/shared/TimeEntryModal';
import { useNotificationsStore, type Notification, type NotifType } from '@/lib/notificationsStore';
import { resolveNotificationLink } from '@/lib/notificationRouting';
import { useMatterBreadcrumbStore } from '@/lib/matterBreadcrumbStore';
import { SidebarNavList, useSidebarSections } from './Sidebar';

interface TopBarProps {
  onOpenPalette?: () => void;
}

export function TopBar({ onOpenPalette }: TopBarProps) {
  const user = useAuthStore((s) => s.user);
  const chambers = useAuthStore((s) => s.chambers);
  const chambersMode = useChambersMode();
  // Below `lg` the sidebar is not rendered, so the slide-out drawer carries the whole menu.
  const navSections = useSidebarSections();
  const navHasSettingsLink = navSections.some((section) =>
    section.items.some((item) => item.to.endsWith('/settings')),
  );
  const navigate = useNavigate();
  const logout = useLogout();
  const breadcrumb = useMatterBreadcrumbStore((s) => s.breadcrumb);

  const isAdmin = user?.memberType === 'admin' || user?.memberType === 'staff';
  // An external solicitor is not a chambers member: without this they fell through to
  // '/barrister', so every link built from basePath aimed at a route they cannot open.
  const isExternal = user?.memberType === 'solicitor';
  const basePath = isAdmin
    ? '/admin'
    : user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk'
    ? '/clerk'
    : isExternal
    ? '/solicitor'
    : '/barrister';

  const [menuOpen, setMenuOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [timeEntryOpen, setTimeEntryOpen] = useState(false);
  const [stoppedMs, setStoppedMs] = useState(0);
  const [notifFilter, setNotifFilter] = useState<'all' | 'hearings' | 'comms' | 'financial' | 'tasks'>('all');

  // Real notifications backed by the API. Polls every 30s + on focus.
  const notifications = useNotificationsStore((s) => s.notifications);
  const unreadCount = useNotificationsStore((s) => s.getUnreadCount('admin'));
  const markNotifRead = useNotificationsStore((s) => s.markRead);
  const markAllNotifsRead = useNotificationsStore((s) => s.markAllRead);
  const dismissNotif = useNotificationsStore((s) => s.dismiss);
  const menuRef = useRef<HTMLDivElement>(null);
  const createRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  // Timer state
  const running = useTimerStore((s) => s.running);
  const startedAt = useTimerStore((s) => s.startedAt);
  const accumulatedMs = useTimerStore((s) => s.accumulatedMs);
  const startTimer = useTimerStore((s) => s.start);
  const pauseTimer = useTimerStore((s) => s.pause);
  const setTimerMatterId = useTimerStore((s) => s.setMatterId);

  // Tick — re-render every second while running
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  const elapsedMs = accumulatedMs + (running && startedAt ? Date.now() - startedAt : 0);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
      if (createRef.current && !createRef.current.contains(e.target as Node)) setCreateOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`;

  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';

  const createOptions = isAdmin
    ? [
        { label: 'New matter', to: `${basePath}/matters/new` },
        { label: 'New hearing', to: `${basePath}/hearings/new` },
        { label: 'New client', to: `${basePath}/contacts/new` },
        { label: 'New task', to: `${basePath}/tasks` },
        { label: 'Add judgment', to: `${basePath}/judgments/new` },
      ]
    : isClerk
    ? [
        { label: 'New matter', to: `${basePath}/matters/new` },
        { label: 'New hearing', to: `${basePath}/hearings/new` },
        { label: 'New client', to: `${basePath}/contacts/new` },
        { label: 'New task', to: `${basePath}/tasks` },
      ]
    : [
        // A pupil holds no `case.create` — `POST /matters` answers 403 (verified live on the dev
        // stack). This is the barrister branch, the only one a pupil reaches; the admin and clerk
        // branches above are untouched. "New client" and "New task" are deliberately left as-is.
        ...(canCreateMatter(user?.memberType)
          ? [{ label: 'New matter', to: `${basePath}/matters/new` }]
          : []),
        { label: 'New client', to: `${basePath}/clients/new` },
        { label: 'New task', to: `${basePath}/tasks` },
      ];

  const handleNotificationClick = (n: Notification) => {
    if (!n.read) markNotifRead(n.id);
    setNotifOpen(false);
    const target = resolveNotificationLink(n, user);
    if (target) navigate(target);
  };

  const stopAndSave = () => {
    const snap = accumulatedMs + (running && startedAt ? Date.now() - startedAt : 0);
    pauseTimer();
    setStoppedMs(snap);
    setTimeEntryOpen(true);
  };

  return (
    <>
    {/*
      THE BAR HAS TO FIT THE NARROWEST PHONE, because it sits OUTSIDE <main>'s `overflow-x-clip`:
      anything it cannot fit widens the document itself, and then every page in the app drags
      left and right, on every screen, for every role. It needed 374px — so a 360px Android, and
      any iPhone with Display Zoom switched on (which reports 320px however large the handset
      is), dragged sideways everywhere. Nothing here is hidden to achieve that: what gives way
      below `sm` is the two decorative chevrons, the idle timer readout — it says 00:00:00, which
      is no information — and the separate Stop button, which does what tapping the readout
      already does. Measured at 320px with a timer running, which is the widest this gets.
    */}
    <header className="sticky top-0 z-30 flex h-14 items-center gap-0.5 border-b border-border/60 bg-card/95 px-1.5 backdrop-blur-sm sm:gap-2 sm:px-3 md:gap-3 md:px-4 lg:gap-4 lg:px-6">
      {/* Menu button — visible below lg, where the sidebar is not rendered */}
      <button
        onClick={() => setMobileMenuOpen(true)}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted lg:hidden"
        aria-label="Open menu"
      >
        <MenuIcon className="h-5 w-5" />
      </button>

      {/* Search - hidden below sm, where the icon button below takes its place */}
      <div className="hidden min-w-0 flex-1 items-center gap-3 sm:flex">
        <div className="relative min-w-0 max-w-3xl flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <button
            type="button"
            onClick={() => onOpenPalette?.()}
            className="h-9 w-full cursor-pointer truncate whitespace-nowrap rounded-lg border border-border/60 bg-muted/20 pl-9 pr-4 text-left text-sm text-muted-foreground transition-all duration-200 hover:border-brand-300 hover:bg-card xl:pr-16"
          >
            Search or press Ctrl+K…
          </button>
          <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground xl:block">
            Ctrl K
          </kbd>
        </div>
      </div>

      {/* Phone search — the icon alone. A label here only ever had room for a letter and a
          full stop ("S."), and tapping opens the full search screen anyway. */}
      <button
        onClick={onOpenPalette}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-muted/20 text-muted-foreground sm:hidden"
        aria-label="Search"
      >
        <Search className="h-4 w-4" />
      </button>

      {/* Matter identity — shown when viewing a matter detail page */}
      {breadcrumb && (
        <div className="hidden min-w-0 items-center gap-2 lg:flex">
          <span className="shrink-0 text-xs text-muted-foreground">{breadcrumb.number}</span>
          <span className="text-xs font-medium text-foreground truncate max-w-[260px]">{breadcrumb.name}</span>
          <Badge variant="outline" className="text-[10px]">{breadcrumb.status.replace(/_/g, ' ')}</Badge>
        </div>
      )}

      {/* Right cluster */}
      <div className="flex flex-1 items-center justify-between gap-1 sm:flex-none sm:shrink-0 sm:justify-end md:gap-2">
        {/* Timer - simplified on mobile */}
        {!isExternal && (
          <div
            className={cn(
              'flex h-10 shrink-0 items-center gap-0.5 rounded-md border px-1 transition sm:gap-1 sm:px-1.5',
              running
                ? 'border-brand-600 bg-brand-50 dark:bg-brand-900/30'
                : 'border-border bg-card hover:border-brand-200',
            )}
          >
            {running ? (
              <button
                onClick={pauseTimer}
                className="flex h-7 w-7 items-center justify-center rounded text-amber-600 dark:text-amber-400 transition hover:bg-amber-50 dark:bg-amber-500/10 dark:hover:bg-amber-900/20"
                title="Pause"
              >
                <Pause className="h-4 w-4" weight="fill" />
              </button>
            ) : (
              <button
                onClick={() => {
                  if (breadcrumb) setTimerMatterId(breadcrumb.id);
                  startTimer();
                }}
                className="flex h-7 w-7 items-center justify-center rounded text-emerald-600 dark:text-emerald-400 transition hover:bg-emerald-50 dark:bg-emerald-500/10 dark:hover:bg-emerald-900/20"
                title={breadcrumb ? `Start timer — ${breadcrumb.number}` : 'Start timer'}
              >
                <Play className="h-4 w-4" weight="fill" />
              </button>
            )}
            <button
              onClick={() => {
                if (running) pauseTimer();
                const snap = accumulatedMs + (running && startedAt ? Date.now() - startedAt : 0);
                setStoppedMs(snap);
                setTimeEntryOpen(true);
              }}
              className={cn(
                'tabular px-0.5 text-xs font-semibold transition sm:px-2 sm:text-sm',
                // 00:00:00 is 62px of nothing on a 320px bar. The pill still shows, and still
                // starts the timer; the readout arrives with something to read.
                elapsedMs > 0 ? 'text-foreground' : 'hidden text-muted-foreground sm:block',
              )}
              title={elapsedMs > 0 ? 'Save time entry' : 'Click play to start'}
            >
              {formatElapsed(elapsedMs)}
            </button>
            {elapsedMs > 0 && (
              <button
                onClick={stopAndSave}
                className="hidden h-7 w-7 items-center justify-center rounded text-red-600 dark:text-red-400 transition hover:bg-red-50 dark:bg-red-500/10 dark:hover:bg-red-900/20 sm:flex"
                title="Stop & save"
              >
                <Stop className="h-4 w-4" weight="fill" />
              </button>
            )}
          </div>
        )}

        {/* Recent time entries - hidden on mobile */}
        {!isExternal && (
          <button
            className="hidden h-8 w-8 items-center justify-center rounded-lg text-muted-foreground/70 transition-all duration-200 hover:bg-muted/60 hover:text-foreground md:flex"
            aria-label="Recent time entries"
            onClick={() => navigate(`${basePath}/fees`)}
          >
            <Clock className="h-5 w-5" />
          </button>
        )}

        {/* New */}
        {!isExternal && (
          <div ref={createRef} className="relative">
            <button
              onClick={() => setCreateOpen((s) => !s)}
              className="inline-flex h-9 shrink-0 items-center gap-1 rounded-lg bg-brand-600 px-1.5 text-sm font-medium text-white shadow-sm shadow-brand-600/20 transition-all duration-200 hover:bg-brand-700 hover:shadow-md hover:shadow-brand-600/25 sm:gap-1.5 sm:px-4"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">New</span>
              <ChevronDown className="hidden h-3.5 w-3.5 opacity-80 sm:block" />
            </button>
            {createOpen && (
              <div className="absolute right-0 top-11 w-56 overflow-hidden rounded-xl border border-border/60 bg-card py-1.5 shadow-pop">
                {createOptions.map((opt) => (
                  <button
                    key={opt.to}
                    onClick={() => {
                      setCreateOpen(false);
                      navigate(opt.to);
                    }}
                    className="block w-full px-4 py-2 text-left text-sm text-foreground transition hover:bg-muted"
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div ref={notifRef} className={`relative ${isExternal ? 'hidden' : ''}`}>
          <button
            className="relative flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground/70 transition-all duration-200 hover:bg-muted/60 hover:text-foreground"
            aria-label="Notifications"
            onClick={() => { setNotifOpen((s) => !s); setNotifFilter('all'); }}
          >
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-card">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
          {notifOpen && (
            <div className="absolute right-0 top-12 w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-border bg-card shadow-pop sm:w-[400px]">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-foreground">Notifications</span>
                  {unreadCount > 0 && (
                    <span className="rounded-full bg-brand-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </div>
                {unreadCount > 0 && (
                  <button
                    onClick={() => markAllNotifsRead()}
                    className="text-xs font-medium text-brand-600 dark:text-brand-400 transition hover:text-brand-700 dark:hover:text-brand-300"
                  >
                    Mark all read
                  </button>
                )}
              </div>

              {/* Category filter tabs */}
              <div className="flex gap-0 border-b border-border">
                {([
                  { key: 'all',       label: 'All' },
                  { key: 'hearings',  label: 'Hearings' },
                  { key: 'comms',     label: 'Comms' },
                  { key: 'financial', label: 'Financial' },
                  { key: 'tasks',     label: 'Tasks' },
                ] as const).map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setNotifFilter(tab.key)}
                    className={cn(
                      'flex-1 py-2 text-[11px] font-medium transition border-b-2',
                      notifFilter === tab.key
                        ? 'border-brand-600 text-brand-600 dark:text-brand-400'
                        : 'border-transparent text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Notification list */}
              {(() => {
                const NOTIF_CAT: Record<string, string> = {
                  hearing_booked: 'hearings', hearing_assigned: 'hearings',
                  hearing_reminder: 'hearings', hearing_outcome_pending: 'hearings',
                  message_received: 'comms', conference_requested: 'comms',
                  conference_scheduled: 'comms', conference_cancelled: 'comms',
                  conference_completed: 'comms',
                  clerk_coordination_requested: 'comms', clerk_coordination_updated: 'comms', clerk_coordination_cancelled: 'comms',
                  fee_note_issued: 'financial', fee_note_paid: 'financial',
                  fee_note_pending: 'financial',
                  task_assigned: 'tasks', task_due: 'tasks',
                };
                const filtered = notifFilter === 'all'
                  ? notifications
                  : notifications.filter((n) => NOTIF_CAT[n.type] === notifFilter);

                if (filtered.length === 0) {
                  return (
                    <div className="px-4 py-10 text-center">
                      <Bell className="mx-auto mb-2 h-6 w-6 text-muted-foreground/30" />
                      <p className="text-sm text-muted-foreground">
                        {notifFilter === 'all' ? 'You\'re all caught up.' : `No ${notifFilter} notifications.`}
                      </p>
                    </div>
                  );
                }

                return (
                  <div className="max-h-[420px] overflow-y-auto">
                    {filtered.map((n) => (
                      <NotificationRow
                        key={n.id}
                        n={n}
                        onOpen={() => handleNotificationClick(n)}
                        onDismiss={() => dismissNotif(n.id)}
                      />
                    ))}
                  </div>
                );
              })()}

              {/* Footer — link to full notification centre */}
              <div className="border-t border-border py-2 text-center">
                <button
                  onClick={() => { setNotifOpen(false); navigate(`${basePath}/notifications`); }}
                  className="text-xs font-medium text-brand-600 dark:text-brand-400 transition hover:text-brand-700 dark:hover:text-brand-300"
                >
                  View all notifications →
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Divider - hidden on mobile */}
        <div className="mx-1 hidden h-8 w-px bg-border md:block" />

        {/* User menu - simplified on mobile */}
        <div ref={menuRef} className="relative">
          <button
            onClick={() => setMenuOpen((s) => !s)}
            className={cn(
              'flex items-center gap-2 rounded-md px-1 py-1.5 transition hover:bg-muted sm:px-2',
            )}
          >
            <Avatar fallback={initials} size="sm" />
            <div className="hidden text-left lg:block">
              <div className="text-sm font-semibold leading-tight text-foreground">
                {user?.firstName} {user?.lastName}
              </div>
              <div className="text-xs capitalize leading-tight text-muted-foreground">
                {user?.memberType?.replace('_', ' ')}
              </div>
            </div>
            <ChevronDown className="hidden h-3.5 w-3.5 text-muted-foreground sm:block" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-11 w-60 overflow-hidden rounded-xl border border-border/60 bg-card py-1.5 shadow-pop">
              <div className="border-b border-border px-4 py-3">
                <div className="text-sm font-semibold text-foreground">
                  {user?.firstName} {user?.lastName}
                </div>
                <div className="truncate text-xs text-muted-foreground">{user?.email}</div>
              </div>
              <div className="my-1 border-t border-border" />
              <button
                onClick={() => {
                  setMenuOpen(false);
                  logout.mutate();
                }}
                className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-sm text-danger transition hover:bg-danger-bg"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>

    </header>

    {/* Time entry modal — rendered outside header to avoid z-index stacking context */}
    <TimeEntryModal
      open={timeEntryOpen}
      initialMs={stoppedMs}
      onClose={() => setTimeEntryOpen(false)}
    />

    {/* Mobile + tablet slide-out menu — the SAME navigation the desktop sidebar shows.
        It used to be a hand-written list of five shortcuts (New matter / client / task,
        Settings, Sign out), every one of which is already on screen at these widths via the
        "+ New" button and the avatar menu. So below `lg` there was no way to reach most of
        the product at all. It now renders `SidebarNavList` from the same `useSidebarSections()`
        the sidebar uses, so the two can never drift apart. */}
    {mobileMenuOpen && (
      <div className="fixed inset-0 z-50 lg:hidden">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
        />
        {/* Panel */}
        <div className="absolute inset-y-0 left-0 flex w-[280px] flex-col bg-ink-900 shadow-2xl">
          {/* Brand — matches the desktop sidebar header */}
          <div className="flex h-16 shrink-0 items-center justify-between border-b border-ink-800/50 px-4">
            <div className="flex min-w-0 items-center gap-3">
              <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-lg shadow-brand-600/20">
                <Scales className="h-[18px] w-[18px]" weight="bold" />
              </div>
              <div className="min-w-0">
                <div className="truncate text-[15px] font-bold leading-tight tracking-tight text-white">
                  Jurivoxa
                </div>
                {/* Mirrors the desktop sidebar header — the workspace type is stated on every
                    screen at every size, not left to be inferred from what is missing. */}
                <div className="mt-0.5 flex items-center gap-1.5">
                  <span className="min-w-0 flex-1 truncate text-[10px] font-medium uppercase tracking-[0.1em] text-ink-400">
                    {chambers?.name ?? workspaceTypeLabel(chambersMode.mode)}
                  </span>
                  <span className="shrink-0 rounded border border-brand-600/30 bg-brand-900/40 px-1 py-px text-[9px] font-semibold uppercase tracking-[0.08em] text-brand-300">
                    {workspaceTypeLabel(chambersMode.mode)}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-ink-400 hover:bg-ink-800 hover:text-white"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/*
            The navigation itself — scrolls, because the admin nav is long.

            Settings and Sign out ride at the END of that scroll, not in a pinned footer.
            The Settings row only appears for roles whose own nav has no settings link: admin
            and barrister navs already carry one, clerk and solicitor navs do not, and the
            avatar menu offers only Sign out — so for a clerk this is the single route to
            `/clerk/settings` below `lg`.
          */}
          <SidebarNavList
            sections={navSections}
            onNavigate={() => setMobileMenuOpen(false)}
            footer={
              <div className="mt-2 border-t border-ink-800/50 pt-3">
                {!navHasSettingsLink && (
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate(`${basePath}/settings`);
                    }}
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-ink-400 transition hover:bg-ink-800/70 hover:text-ink-100"
                  >
                    <Settings className="h-[18px] w-[18px]" />
                    Settings
                  </button>
                )}
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    logout.mutate();
                  }}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-red-400 transition hover:bg-red-900/30"
                >
                  <LogOut className="h-[18px] w-[18px]" />
                  Sign out
                </button>
              </div>
            }
          />
        </div>
      </div>
    )}
    </>
  );
}

// ─── Notification row in the bell dropdown ─────────────────────
//
// Professional, neutral palette (matches the enterprise legal SaaS theme).
// One small Phosphor icon per type — no emoji, no rainbow status colours.

const NOTIF_ICON: Record<string, { Icon: React.ElementType; tone: string }> = {
  // Messaging
  message_received:         { Icon: ChatTeardropDots, tone: 'text-brand-600 bg-brand-50 dark:bg-brand-500/20 dark:text-brand-400' },
  // Briefs / allocation
  brief_received:           { Icon: Briefcase,        tone: 'text-amber-700 bg-amber-50 dark:bg-amber-500/20 dark:text-amber-400' },
  brief_accepted:           { Icon: CheckCircle,      tone: 'text-emerald-700 bg-emerald-50 dark:bg-emerald-500/20 dark:text-emerald-400' },
  brief_returned:           { Icon: ArrowsClockwise,  tone: 'text-slate-700 bg-slate-100 dark:bg-slate-700/40 dark:text-slate-400' },
  brief_declined:           { Icon: ArrowsClockwise,  tone: 'text-slate-700 bg-slate-100 dark:bg-slate-700/40 dark:text-slate-400' },
  brief_withdrawn:          { Icon: ArrowsClockwise,  tone: 'text-slate-700 bg-slate-100 dark:bg-slate-700/40 dark:text-slate-400' },
  // Conferences
  conference_requested:     { Icon: Users,            tone: 'text-violet-700 bg-violet-50 dark:bg-violet-500/20 dark:text-violet-400' },
  conference_scheduled:     { Icon: CalendarCheck,    tone: 'text-violet-700 bg-violet-50 dark:bg-violet-500/20 dark:text-violet-400' },
  conference_cancelled:     { Icon: XCircle,          tone: 'text-slate-700 bg-slate-100 dark:bg-slate-700/40 dark:text-slate-400' },
  conference_completed:     { Icon: CheckCircle,      tone: 'text-emerald-700 bg-emerald-50 dark:bg-emerald-500/20 dark:text-emerald-400' },
  // Hearings
  hearing_booked:           { Icon: Gavel,            tone: 'text-amber-700 bg-amber-50 dark:bg-amber-500/20 dark:text-amber-400' },
  hearing_assigned:         { Icon: Gavel,            tone: 'text-amber-700 bg-amber-50 dark:bg-amber-500/20 dark:text-amber-400' },
  hearing_reminder:         { Icon: Clock,            tone: 'text-amber-700 bg-amber-50 dark:bg-amber-500/20 dark:text-amber-400' },
  hearing_outcome_pending:  { Icon: Warning,          tone: 'text-amber-700 bg-amber-50 dark:bg-amber-500/20 dark:text-amber-400' },
  // Matters / cases
  case_assigned:            { Icon: Scales,           tone: 'text-brand-600 bg-brand-50 dark:bg-brand-500/20 dark:text-brand-400' },
  matter_inactive:          { Icon: Clock,            tone: 'text-slate-600 bg-slate-100 dark:bg-slate-700/40 dark:text-slate-400' },
  conflict_check_required:  { Icon: Warning,          tone: 'text-red-700 bg-red-50 dark:bg-red-500/20 dark:text-red-400' },
  // Tasks
  task_assigned:            { Icon: ClipboardText,    tone: 'text-brand-600 bg-brand-50 dark:bg-brand-500/20 dark:text-brand-400' },
  task_due:                 { Icon: Clock,            tone: 'text-red-700 bg-red-50 dark:bg-red-500/20 dark:text-red-400' },
  // Documents & fees
  document_uploaded:        { Icon: FileText,         tone: 'text-slate-700 bg-slate-100 dark:bg-slate-700/40 dark:text-slate-400' },
  fee_note_issued:          { Icon: CurrencyGbp,      tone: 'text-emerald-700 bg-emerald-50 dark:bg-emerald-500/20 dark:text-emerald-400' },
  fee_note_paid:            { Icon: CurrencyGbp,      tone: 'text-emerald-700 bg-emerald-50 dark:bg-emerald-500/20 dark:text-emerald-400' },
  fee_note_pending:         { Icon: CurrencyGbp,      tone: 'text-amber-700 bg-amber-50 dark:bg-amber-500/20 dark:text-amber-400' },
  // Compliance
  kyc_expiring:             { Icon: Warning,          tone: 'text-amber-700 bg-amber-50 dark:bg-amber-500/20 dark:text-amber-400' },
  limitation_approaching:   { Icon: Warning,          tone: 'text-red-700 bg-red-50 dark:bg-red-500/20 dark:text-red-400' },
  judgment_reserved:        { Icon: Gavel,            tone: 'text-violet-700 bg-violet-50 dark:bg-violet-500/20 dark:text-violet-400' },
  clerk_coordination_requested: { Icon: Users,        tone: 'text-violet-700 bg-violet-50 dark:bg-violet-500/20 dark:text-violet-400' },
  clerk_coordination_updated:   { Icon: CalendarCheck, tone: 'text-amber-700 bg-amber-50 dark:bg-amber-500/20 dark:text-amber-400' },
  clerk_coordination_cancelled: { Icon: XCircle,      tone: 'text-red-700 bg-red-50 dark:bg-red-500/20 dark:text-red-400' },
};

function relativeTime(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} h ago`;
  return new Date(ms).toLocaleDateString([], { day: '2-digit', month: 'short' });
}

interface NotificationRowProps {
  n: Notification;
  onOpen: () => void;
  onDismiss: () => void;
}

function NotificationRow({ n, onOpen, onDismiss }: NotificationRowProps) {
  const meta = NOTIF_ICON[n.type as NotifType] ?? { Icon: Bell, tone: 'text-slate-600 bg-slate-100 dark:bg-slate-700/40 dark:text-slate-400' };
  const { Icon, tone } = meta;
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'group flex w-full items-start gap-3 border-b border-border/60 px-4 py-3 text-left transition last:border-b-0',
        n.read ? 'bg-card hover:bg-muted/40' : 'bg-brand-50/40 hover:bg-brand-50 dark:bg-brand-500/10 dark:hover:bg-brand-500/15',
        n.priority === 'critical' && 'border-l-[3px] border-l-red-500',
        n.priority === 'high' && 'border-l-[3px] border-l-amber-500',
      )}
    >
      <div className={cn('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md', tone)}>
        <Icon className="h-4 w-4" weight="duotone" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className={cn('truncate text-sm leading-snug', n.read ? 'text-foreground' : 'font-semibold text-foreground')}>
            {n.title}
          </p>
          {!n.read && <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-600" />}
        </div>
        {n.detail && (
          <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-muted-foreground">
            {n.detail}
          </p>
        )}
        <span className="mt-0.5 block text-xs uppercase tracking-[0.04em] text-muted-foreground/80">
          {relativeTime(n.createdAt)}
        </span>
      </div>
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            onDismiss();
          }
        }}
        className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground/60 opacity-0 transition hover:bg-border hover:text-foreground group-hover:opacity-100"
        title="Dismiss"
      >
        <X className="h-3 w-3" />
      </span>
    </button>
  );
}
